import * as React from "react";
import {
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  DataGrid,
} from "@mui/x-data-grid";
import { useEffect, useRef, useState } from "react";
import { useAxios } from "../../utils/useAxios";
import { useNavigate } from "react-router";
import { useModal } from "../../hooks/useModal";
import {
  EyeIcon,
  HorizontaLDots,
  ListIcon,
  PencilIcon,
  PlusIcon,
  Reload,
  RemoveIcon,
} from "../../icons";
import { color } from "@mui/system";

import { createTheme } from "@mui/material/styles";
import type {} from "@mui/x-data-grid/themeAugmentation";
import { MyDataTable } from "./styles/DataTable.style";
import { Button, Link } from "@mui/material";
import { TopMenu } from "./json/TopMenu";
import { useResizeObserver } from "../../utils/Resize";
import { bsEncrypt } from "../../utils/storage";
import { encryptObject } from "../../utils/crypt";
import { renderLinkCell } from "../../utils/renderUtils";
import { useFilterData } from "../../context/TableDataContext";
import { useCountData } from "../../context/CountDataContext";

interface Props {
  formId: string;
  getEdit: (id: number, target: string) => void;
  formIntData?: any;
  addButton: any;
}

export default function DataTable({
  formId,
  getEdit,
  formIntData,
  addButton,
}: Props) {
  const [finalData, setFinalData] = useState<any>([]);
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");

  const { filterData } = useFilterData();

  const { countData } = useCountData();

  const divRef = useRef<HTMLDivElement>(null);
  // const [width, setWidth] = useState(0);
  const { width, height } = useResizeObserver(divRef);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const localStorageKey = `columnVisibility-${formId}`;
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<any>(
    () => {
      // Load from localStorage on init
      const saved = localStorage.getItem(localStorageKey);
      return saved ? JSON.parse(saved) : { id: true };
    },
  );

  useEffect(() => {
    localStorage.setItem(
      localStorageKey,
      JSON.stringify(columnVisibilityModel),
    );
  }, [columnVisibilityModel]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenRowId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const [openRowId, setOpenRowId] = useState<number | null>(null);

  const toggleDropdown = (rowId: number) => {
    setOpenRowId((prevId) => (prevId === rowId ? null : rowId));
  };

  const { res, mutate, isPending, error } = useAxios({
    url: "/api/auth/tabledata",
  });

  const {
    res: deleteRes,
    mutate: deleteMutate,
    isPending: deleteIsPending,
    error: deleteError,
  } = useAxios({
    url: "/api/auth/deleteRecord",
  });

  const deleteRed = (id: any) => {
    deleteMutate({
      delete_id: id,
      formId: formId,
    });
  };

  useEffect(() => {
    if (formId) {
      mutate({ formId, formIntData });
    }
  }, [formId, deleteRes]); // Added dependency

  useEffect(() => {
    if (filterData) {
      setFinalData(filterData);
    }
  }, [filterData]);

  useEffect(() => {
    if (countData) {
      setFinalData(countData);
    }
  }, [countData]);

  if (isPending) return <p>Loading...</p>;
  if (error) return <p>Error fetching data</p>;

  const handleEdit = (id: number) => {
    getEdit(id, "edit");
    // Your edit logic here
  };

  const handleDelete = (id: number) => {
    deleteRed(id);
  };
  const handleViwe = (id: number) => {
    formIntData?.idShow || false;
    if (formIntData?.detailsPage) {
      navigate(formIntData?.detailsPage + "/" + id);
    } else {
      if (formId === "carrier") {
        const datasource = encryptObject({
          carrier_id: id,
        });
        navigate("form?id=" + datasource);
      } else if (formId === "trailer") {
        const datasource = encryptObject({
          trailer_id: id,
        });
        navigate(`form?id=` + datasource);
      } else if (formId === "vehicle") {
        const datasource = encryptObject({
          vehicle_id: id,
        });
        navigate(`form?id=` + datasource);
      } else if (formId === "drivers") {
        const datasource = encryptObject({
          driver_id: id,
        });
        navigate(`form?id=` + datasource);
      } else if (formId === "load") {
        const datasource = encryptObject({
          load_id: id,
        });
        navigate(`form?id=` + datasource);
      } else {
        navigate(`form?id=` + id);
      }
    }
  };

  const getTopmenu = TopMenu.filter((item: any) => item?.formId === formId);

  function CustomToolbar() {
    return (
      <GridToolbarContainer
        sx={{
          padding: "10px 10px",
          background: "rgb(255 255 255 / var(--tw-bg-opacity, 1))",
        }}
        className="flex justify-between items-center w-full px-2 py-5 border-b border-gray-400"
      >
        <div className="flex gap-2">
          {/* <Button startIcon={<PlusIcon width={16} height={16} />}>ADD NEW</Button> */}
          {addButton}
          <Button startIcon={<Reload width={16} height={16} />}>Reload</Button>
        </div>
        <div className="flex gap-2">
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarDensitySelector />
          <GridToolbarExport />
        </div>
      </GridToolbarContainer>
    );
  }
  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      //disableColumnMenu: true,
      sortable: false,
      headerClassName: "sticky-header", // Add header class
      // cellClassName: "sticky-cell ", // Add sticky cell class
      //cellClassName: "sticky left-0 bg-white shadow z-10", // Sticky column
      renderCell: (params: any) => {
        const isOpen = openRowId === params.row.id;
        return (
          <div
            className=" gap-1 align-middle h-full"
            ref={isOpen ? dropdownRef : null}
          >
            <button
              data-dropdown-toggle="dropdown"
              onClick={(e) => {
                e.stopPropagation(); // Prevent row click
                toggleDropdown(params.row.id);
              }}
              className="text-white bg-rose-500 hover:bg-blue-800  focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-1 text-center inline-flex items-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
              type="button"
            >
              Actions
              <svg
                className="w-2.5 h-2.5 ms-3"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 10 6"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 4 4 4-4"
                />
              </svg>
            </button>
            {isOpen && (
              <div className="z-9999 absolute bg-gray-50 divide-y divide-gray-100 rounded-lg shadow-xl  dark:bg-gray-700">
                <ul
                  className="py-2 text-sm text-gray-700 dark:text-gray-200"
                  aria-labelledby="dropdownDefaultButton"
                >
                  <li>
                    <Button
                      className="w-full"
                      sx={{
                        justifyContent: "flex-start",
                      }}
                      startIcon={<EyeIcon fill="currentColor" />}
                      onClick={() => handleViwe(params.row.id)}
                    >
                      View
                    </Button>
                  </li>
                  <li>
                    <Button
                      className="w-full"
                      sx={{
                        justifyContent: "flex-start",
                      }}
                      startIcon={<PencilIcon />}
                      onClick={() => handleEdit(params.row.id)}
                    >
                      Edit
                    </Button>
                  </li>
                  <li>
                    <Button
                      className="w-full"
                      sx={{
                        justifyContent: "flex-start",
                      }}
                      startIcon={<ListIcon />}
                    >
                      View History
                    </Button>
                  </li>
                  <li>
                    <Button
                      className="w-full"
                      sx={{
                        justifyContent: "flex-start",
                      }}
                      startIcon={<RemoveIcon />}
                      onClick={() => handleDelete(params.row.id)}
                    >
                      Delete
                    </Button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        );
      },
    },
    ...(res?.data?.headers || []).map((col: any) => ({
      field: col.field,
      headerName: col.headerName || col.title || col.field,
      width: 180,
      renderCell: (params: any) => {
        //const value = params.row[col.field];

        const linkColumnConfig = {
          carrier: ["carrier_name"],
          trailer: ["number"],
          vehicle: ["number"],
          drivers: ["first_name"],
          load: ["id"],
        };

        const value = params.row[col.field];
        return renderLinkCell(
          formId,
          col.field,
          value,
          params.row.id,
          handleViwe,
          linkColumnConfig,
        );
      },
    })),
  ];

  // filterData
  // countData

  const rows = finalData.length > 0 ? finalData : res?.data?.data || [];
  // const rows = filterData
  //   ? filterData
  //   : countData
  //     ? countData
  //     : res?.data?.data || [];

  const filteredRows = rows.filter((row: any) =>
    Object.values(row).some((value) =>
      String(value).toLowerCase().includes(searchText.toLowerCase()),
    ),
  );

  return (
    <>
      <div className="">
        {formIntData?.topMenus && (
          <div className="flex items-end text-right justify-end gap-2 mb-2">
            {getTopmenu[0]?.menus?.map((getItem: any, index: number) => (
              <div key={index}>
                <Button
                  size="small"
                  sx={{
                    boxShadow: "0",
                  }}
                  color={getItem?.color}
                  variant={getItem?.verient}
                  startIcon={<i className={getItem?.icon}></i>}
                >
                  {getItem?.label}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="bg-white">
        {/* <div ref={divRef}></div> */}
        <div>
          <div
            ref={divRef}
            style={{
              // width: "300px",
              minWidth: "150px",
              height: "0px",
              // border: "1px solid gray",
              padding: "0px",
            }}
          >
            {/* {width} */}
          </div>
          {/* <p>Width: {Math.round(width)}px</p>
          <p>Height: {Math.round(height)}px</p> */}
        </div>

        {/* <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-400">
          Search
        </label>
        <input
          type="text"
          placeholder="Search..."
          className="h-9 border w-[300px] px-4 py-1 mb-3 text-sm shadow-theme-xs placeholder:text-gray-400 focus:outline-none focus:ring bg-white text-gray-800 border-gray-300 focus:border-brand-300 focus:ring focus:ring-brand-500/10"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        /> */}
        {/* <div></div> */}
        <div
          style={{
            width: "100%",
            minHeight: "450px",

            overflow: "hidden",
            position: "relative",
            //  display:"none"
          }}
        >
          <MyDataTable
            sx={{
              // height: "450px",
              width: `${width ? width : 1000}px`,
              position: "absolute",
              borderRadius: "0",
              // display:"none"
            }}
            rows={filteredRows}
            columns={columns as any}
            getRowId={(row) =>
              formId === "my_forms" ||
              formId === "my_forms_sections" ||
              formId === "my_forms_columns"
                ? row.formid
                : row.id
            }
            loading={isPending}
            // pageSizeOptions={[10]}
            // pagination
            paginationMode="client"
            rowHeight={40}
            columnHeaderHeight={30}
            slots={{
              toolbar: CustomToolbar,
            }}
            // initialState={{
            //   pagination: {
            //     paginationModel: { pageSize: 8, page: 0 },
            //   },
            // }}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) =>
              setColumnVisibilityModel(newModel)
            }
            getRowClassName={(params: any) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
            }
          />
        </div>
      </div>
    </>
  );
}
